import { useCallback, useState } from 'react';
import { TELEHEALTH_SERVICES_LANDING_URL } from '../../constants';
import { useCTA } from '../../hooks/useCTA';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useSearch } from '../../hooks/useSearch';
import { IconHealthEngine } from '../icons/IconHealthEngine';
import { IconHouse } from '../icons/IconHouse';
import { IconPersonUnfilled } from '../icons/IconPersonUnfilled';
import { IconSearch } from '../icons/IconSearch';
import { IconTelehealthPhone } from '../icons/IconTelehealthPhone';
import {
  ButtonContainer,
  NavigationBarContainer,
  NavigationBarInner,
  SearchButtonContainer,
  TelehealthCTADot,
  Text,
  TextContainer,
} from './BottomNavigationBar.styles';

type Button = {
  text: string;
  link?: string;
  icon:
    | typeof IconHouse
    | typeof IconSearch
    | typeof IconTelehealthPhone
    | typeof IconHealthEngine
    | typeof IconPersonUnfilled;
  isTelehealthButton?: boolean;
  isSearchButton?: boolean;
};

const TELE_DOT_KEY = 'TELE_DOT_KEY';

export const BottomNavigationBar = () => {
  const IconColor = '#6A7B7D';
  const IconHeight = '24';

  const { toggleMobileSearch } = useSearch();

  // We use both local storage and the useCTA() hook because users
  // that aren't logged in must also be able to see the CTA dot
  const { cta, updateCta } = useCTA();
  const [showTeleDot, setShowTeleDot] = useLocalStorage<boolean>(TELE_DOT_KEY, true);

  // Use local storage if the user is logged out (i.e `cta` is empty), else use
  // the cta
  const [CTADotIsVisible, setCTADotIsVisible] = useState<boolean>(() => {
    if (!Object.keys(cta).length) {
      return showTeleDot;
    } else {
      return cta.showTelehealthCTA ?? true;
    }
  });

  const buttons: Button[] = [
    { text: 'Home', link: '/', icon: IconHouse },
    { text: 'Find & Book', icon: IconSearch, isSearchButton: true },
    {
      text: 'GP Telehealth',
      link: `${TELEHEALTH_SERVICES_LANDING_URL}?source=mbn&icid=tel:mbn::::gp:gpt:::aug22`,
      icon: IconTelehealthPhone,
      isTelehealthButton: true,
    },
    { text: 'My Care Team', link: '/my-account/favourites', icon: IconHealthEngine },
    { text: 'Account', link: '/my-account/profiles', icon: IconPersonUnfilled },
  ];

  const updateCTAState = useCallback(() => {
    if (cta?.showTelehealthCTA) {
      updateCta({ showTelehealthCTA: false });
    }
  }, [cta?.showTelehealthCTA, updateCta]);

  const updateCTADotVisibility = useCallback(() => {
    updateCTAState();
    setShowTeleDot(false);
    setCTADotIsVisible(false);
  }, [updateCTAState, setShowTeleDot]);

  return (
    <>
      <NavigationBarContainer>
        <NavigationBarInner>
          {buttons.map((button, index) => (
            <>
              {!button.isSearchButton ? (
                <ButtonContainer
                  key={index}
                  to={button.link ?? ''}
                  data-link-id={`Mobile Bottom Nav ${button.text}`}
                  onClick={() => {
                    button.isTelehealthButton && updateCTADotVisibility();
                  }}
                >
                  <button.icon key={index} color={IconColor} height={IconHeight}></button.icon>
                  <TextContainer>
                    <Text>{button.text}</Text>
                  </TextContainer>
                  {button.isTelehealthButton && CTADotIsVisible && <TelehealthCTADot />}
                </ButtonContainer>
              ) : (
                <SearchButtonContainer
                  key={index}
                  onClick={() => toggleMobileSearch()}
                  data-link-id={`Mobile Bottom Nav ${button.text}`}
                >
                  <button.icon key={index} color={IconColor} height={IconHeight}></button.icon>
                  <TextContainer>
                    <Text>{button.text}</Text>
                  </TextContainer>
                </SearchButtonContainer>
              )}
            </>
          ))}
        </NavigationBarInner>
      </NavigationBarContainer>
    </>
  );
};
